// src/pages/ControlSesiones.js
import React, { useState, useEffect, useMemo } from 'react';
import { db, auth, functions } from '../firebase';
import { collection, collectionGroup, getDocs, getDoc, setDoc, query, where, doc, updateDoc, onSnapshot, deleteDoc } from 'firebase/firestore';
import { sendPasswordResetEmail } from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import PlatformBackButton from '../components/PlatformBackButton';
import { FaUsers, FaSearch, FaUserShield, FaKey, FaEdit, FaLock, FaUnlock, FaPlus, FaTimes, FaShieldAlt, FaLaptop, FaMobileAlt, FaDesktop, FaBuilding, FaTrashAlt, FaCircle, FaUserTie, FaHome, FaClock, FaFilter, FaLaptopHouse, FaTools, FaGooglePlay, FaChartBar } from 'react-icons/fa';
import { FiAlertTriangle, FiCheckCircle, FiEye, FiEyeOff, FiEdit2, FiLock, FiUnlock, FiKey } from 'react-icons/fi';
import ConfirmModal from '../components/ConfirmModal';
import ReporteEjecutivoAnaliticaPanel from '../components/manager/ReporteEjecutivoAnaliticaPanel';
import Swal from 'sweetalert2';
import './ControlSesiones.css';

const PASSWORD_ENDPOINT = 'https://us-central1-gestionpagosfracc.cloudfunctions.net/setUserPasswordHttp';
const SUSPEND_ENDPOINT = 'https://suspenduserhttp-gb5hv5xxaq-uc.a.run.app';

const parseBrowserName = (userAgent = '') => {
    if (!userAgent) return 'Dispositivo Web / Móvil';
    const ua = userAgent;
    if (ua.includes('Edg/')) return 'Microsoft Edge';
    if (ua.includes('Chrome/')) return 'Google Chrome';
    if (ua.includes('Firefox/')) return 'Mozilla Firefox';
    if (ua.includes('Safari/') && !ua.includes('Chrome/')) return 'Safari';
    if (ua.includes('Android')) return 'Navegador Android / App';
    if (ua.includes('iPhone') || ua.includes('iPad')) return 'iOS Safari / App';
    return 'Dispositivo Web / Móvil';
};

const parseOS = (userAgent = '') => {
    if (!userAgent) return 'Sistema Operativo';
    const ua = userAgent;
    if (ua.includes('Windows NT 10')) return 'Windows 10/11';
    if (ua.includes('Windows NT')) return 'Windows';
    if (ua.includes('Mac OS X')) return 'macOS';
    if (ua.includes('Android')) return 'Android';
    if (ua.includes('iPhone') || ua.includes('iPad')) return 'iOS';
    if (ua.includes('Linux')) return 'Linux';
    return 'Sistema Operativo';
};

const isMobileDevice = (userAgent = '') => {
    const ua = (userAgent || '').toLowerCase();
    return ua.includes('android') || ua.includes('iphone') || ua.includes('ipad') || ua.includes('mobile');
};

const formatFechaCST = (timestamp) => {
    if (!timestamp) return 'Reciente';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    if (isNaN(date.getTime())) return 'Reciente';
    return date.toLocaleString('es-MX', {
        timeZone: 'America/Mexico_City',
        dateStyle: 'medium',
        timeStyle: 'short'
    });
};

const ControlSesiones = () => {
    const [activeMainTab, setActiveMainTab] = useState('usuarios_globales');
    const [usuariosGlobales, setUsuariosGlobales] = useState([]);
    const [usuariosLoading, setUsuariosLoading] = useState(true);
    const [usuariosError, setUsuariosError] = useState('');
    const [editingUser, setEditingUser] = useState(null);
    const [editForm, setEditForm] = useState({ nombre: '', correo: '', usuario: '', telefono: '', contrasena: '', rol: '' });
    const [saving, setSaving] = useState(false);
    const [sendingReset, setSendingReset] = useState('');
    const [resetFeedback, setResetFeedback] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [showPlatformAdminConfirm, setShowPlatformAdminConfirm] = useState(false);
    const [pendingRole, setPendingRole] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [passwordError, setPasswordError] = useState('');
    const [activeTab, setActiveTab] = useState('perfil');
    const [customFields, setCustomFields] = useState([]);

    // Estados para pestaña de Monitoreo Global de Sesiones Activas
    const [allActiveSessions, setAllActiveSessions] = useState([]);
    const [sessionsLoading, setSessionsLoading] = useState(true);
    const [fraccionamientosMap, setFraccionamientosMap] = useState({});
    const [usersMap, setUsersMap] = useState({});
    const [sessionSearchTerm, setSessionSearchTerm] = useState('');
    const [sessionRoleFilter, setSessionRoleFilter] = useState('todos');
    const [sessionDeviceFilter, setSessionDeviceFilter] = useState('todos');

    // Estadísticas para mini tarjeta de Aviso de Privacidad
    const [privacyStats, setPrivacyStats] = useState({
        total: 0,
        accepted: 0,
        pending: 0,
        percentage: 0
    });

    useEffect(() => {
        const usuariosRef = collection(db, 'usuarios');
        const unsub = onSnapshot(usuariosRef, (snap) => {
            const total = snap.docs.length;
            let accepted = 0;
            snap.docs.forEach((docSnap) => {
                const data = docSnap.data();
                if (data.privacyPolicyAccepted === true) {
                    accepted++;
                }
            });
            const pending = Math.max(0, total - accepted);
            const percentage = total > 0 ? Math.round((accepted / total) * 100) : 0;
            setPrivacyStats({ total, accepted, pending, percentage });
        }, (err) => {
            console.warn('[ControlSesiones] Error al consultar estadísticas de aviso de privacidad:', err);
        });

        return () => unsub();
    }, []);

    // Paginación para sesiones activas
    const [currentPage, setCurrentPage] = useState(1);
    const sessionsPerPage = 10;

    useEffect(() => {
        setCurrentPage(1);
    }, [sessionSearchTerm, sessionRoleFilter, sessionDeviceFilter]);

    // Estados para Configuración de App/Sistema (Tercera pestaña)
    const [appConfig, setAppConfig] = useState({
        current_version: '',
        force_update_enabled: false,
        play_store_url: '',
        update_message: '',
        google_play_tracks: {
            produccion: [],
            prueba_interna: []
        }
    });
    const [configLoading, setConfigLoading] = useState(false);
    const [configSaving, setConfigSaving] = useState(false);

    useEffect(() => {
        if (activeMainTab !== 'configuracion_app') return;

        const fetchConfig = async () => {
            setConfigLoading(true);
            try {
                const docRef = doc(db, 'configuracion_sistema', 'app_control');
                const snap = await getDoc(docRef);
                if (snap.exists()) {
                    const data = snap.data();
                    setAppConfig({
                        current_version: data.current_version || '1.0.0',
                        force_update_enabled: data.force_update_enabled ?? false,
                        play_store_url: data.play_store_url || '',
                        update_message: data.update_message || '',
                        google_play_tracks: data.google_play_tracks || {
                            produccion: [
                                { version_code: '8', version_name: '1.0.7', status: 'Rechazado', date: '14 ago 2026 16:37' },
                                { version_code: '6', version_name: '1.0.4', status: 'Activo', date: '31 may 2026 23:05' }
                            ],
                            prueba_interna: [
                                { version_code: '2', version_name: '1.0.1', status: 'Activo', date: '2 mar 2026 14:40' }
                            ]
                        }
                    });
                } else {
                    // Seed inicial por si no existe
                    setAppConfig({
                        current_version: '1.0.4',
                        force_update_enabled: false,
                        play_store_url: 'https://play.google.com/store/apps/details?id=com.fraccionamiento.mifracccionamiento',
                        update_message: 'Tenemos mejoras importantes en el sistema de pagos y seguridad.',
                        google_play_tracks: {
                            produccion: [
                                { version_code: '8', version_name: '1.0.7', status: 'Rechazado', date: '14 ago 2026 16:37' },
                                { version_code: '6', version_name: '1.0.4', status: 'Activo', date: '31 may 2026 23:05' }
                            ],
                            prueba_interna: [
                                { version_code: '2', version_name: '1.0.1', status: 'Activo', date: '2 mar 2026 14:40' }
                            ]
                        }
                    });
                }
            } catch (err) {
                console.error('Error fetching app configuration:', err);
            } finally {
                setConfigLoading(false);
            }
        };

        fetchConfig();
    }, [activeMainTab]);

    const handleSaveConfig = async (e) => {
        if (e) e.preventDefault();
        setConfigSaving(true);
        try {
            const docRef = doc(db, 'configuracion_sistema', 'app_control');
            const snap = await getDoc(docRef);
            const payload = {
                current_version: appConfig.current_version,
                force_update_enabled: appConfig.force_update_enabled,
                play_store_url: appConfig.play_store_url,
                update_message: appConfig.update_message,
                google_play_tracks: appConfig.google_play_tracks,
                updated_at: new Date().toISOString()
            };

            if (snap.exists()) {
                await updateDoc(docRef, payload);
            } else {
                await setDoc(docRef, payload);
            }

            Swal.fire({
                icon: 'success',
                title: 'Configuración guardada',
                text: 'Los parámetros de control de la aplicación se actualizaron correctamente en Firestore.',
                confirmButtonColor: '#4f46e5'
            });
        } catch (err) {
            console.error('Error saving app configuration:', err);
            Swal.fire({
                icon: 'error',
                title: 'Error al guardar',
                text: 'Hubo un problema al actualizar la configuración en Firestore.'
            });
        } finally {
            setConfigSaving(false);
        }
    };

    const handleAddTrackVersion = (trackType) => {
        const updatedTracks = { ...appConfig.google_play_tracks };
        if (!updatedTracks[trackType]) {
            updatedTracks[trackType] = [];
        }
        updatedTracks[trackType].unshift({
            version_code: '',
            version_name: '',
            status: 'Activo',
            date: new Date().toLocaleDateString('es-MX', { day: 'numeric', month: 'short', year: 'numeric' }) + ' ' + new Date().toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit', hour12: false })
        });
        setAppConfig({ ...appConfig, google_play_tracks: updatedTracks });
    };

    const handleRemoveTrackVersion = (trackType, index) => {
        const updatedTracks = { ...appConfig.google_play_tracks };
        updatedTracks[trackType].splice(index, 1);
        setAppConfig({ ...appConfig, google_play_tracks: updatedTracks });
    };

    const handleTrackFieldChange = (trackType, index, field, value) => {
        const updatedTracks = { ...appConfig.google_play_tracks };
        updatedTracks[trackType][index][field] = value;
        setAppConfig({ ...appConfig, google_play_tracks: updatedTracks });
    };

    const [syncingPlayStore, setSyncingPlayStore] = useState(false);

    const handleSyncPlayStore = async () => {
        setSyncingPlayStore(true);
        try {
            const getPlayStoreVersionsFn = httpsCallable(functions, 'getPlayStoreVersions');
            const res = await getPlayStoreVersionsFn();
            
            if (res.data && res.data.success) {
                const fetchedTracks = res.data.tracks;
                
                // Actualizar localmente el estado
                setAppConfig(prev => ({
                    ...prev,
                    google_play_tracks: fetchedTracks
                }));

                // Guardar en Firestore para persistencia
                const docRef = doc(db, 'configuracion_sistema', 'app_control');
                await updateDoc(docRef, {
                    google_play_tracks: fetchedTracks,
                    updated_at: new Date().toISOString()
                });

                Swal.fire({
                    icon: 'success',
                    title: 'Sincronización Exitosa',
                    text: 'Se han obtenido las pistas y versiones de producción e internas directamente de Google Play Developer API.',
                    confirmButtonColor: '#4f46e5'
                });
            } else {
                // Si retorna success: false, mostrar mensaje amigable
                Swal.fire({
                    icon: 'warning',
                    title: 'Falta configurar permisos',
                    html: `<p style="font-size:0.95rem; text-align:left; line-height:1.4;">${res.data?.message || 'No se pudo sincronizar'}</p>`,
                    confirmButtonColor: '#4f46e5'
                });
            }
        } catch (err) {
            console.error('Error syncing Google Play Store versions:', err);
            Swal.fire({
                icon: 'error',
                title: 'Error de Conexión',
                text: 'Hubo un error al llamar a la Cloud Function de sincronización.'
            });
        } finally {
            setSyncingPlayStore(false);
        }
    };

    // Estados para modales de confirmación
    const [confirmModal, setConfirmModal] = useState({
        isOpen: false,
        title: '',
        message: '',
        type: 'info',
        onConfirm: () => { },
        confirmText: 'Confirmar',
        showCancel: true,
    });

    // Cargar usuarios sin fraccionamiento asignado
    useEffect(() => {
        const loadUsuariosSinFracc = async () => {
            try {
                setUsuariosLoading(true);
                const usuariosRef = collection(db, 'usuarios');

                // Buscar usuarios donde fraccionamientoId sea null o no exista
                const qNullId = query(usuariosRef, where('fraccionamientoId', '==', null));
                const snapNullId = await getDocs(qNullId);

                // También buscar todos los usuarios y filtrar los que no tengan fraccionamiento
                const allSnap = await getDocs(usuariosRef);

                const usuarios = [];
                const seenIds = new Set();

                const addUser = (docSnap) => {
                    const data = docSnap.data() || {};
                    if (seenIds.has(docSnap.id)) return;

                    // Solo agregar si no tiene fraccionamiento asignado
                    const hasNoFracc = !data.fraccionamientoId &&
                        !data.fraccionamientoTd &&
                        (!data.fraccionamientosAsignados || data.fraccionamientosAsignados.length === 0);

                    if (hasNoFracc) {
                        seenIds.add(docSnap.id);
                        usuarios.push({ id: docSnap.id, ...data });
                    }
                };

                snapNullId.docs.forEach(addUser);
                allSnap.docs.forEach(addUser);

                setUsuariosGlobales(usuarios);
            } catch (err) {
                console.error('No se pudieron cargar los usuarios sin fraccionamiento', err);
                setUsuariosError('No pudimos cargar los usuarios. Intenta nuevamente.');
            } finally {
                setUsuariosLoading(false);
            }
        };

        loadUsuariosSinFracc();
    }, []);

    // Cargar mapa de fraccionamientos para resolver nombres
    useEffect(() => {
        const fetchFraccionamientos = async () => {
            try {
                const snap = await getDocs(collection(db, 'fraccionamientos'));
                const map = {};
                snap.docs.forEach((docSnap) => {
                    const d = docSnap.data();
                    map[docSnap.id] = d.nombre || d.condominioNombre || d.configuracionRecibos?.condominioNombre || `Fraccionamiento ${docSnap.id}`;
                });
                setFraccionamientosMap(map);
            } catch (err) {
                console.warn('[ControlSesiones] Error cargando nombres de fraccionamientos:', err);
            }
        };
        fetchFraccionamientos();
    }, []);

    // Escuchar sesiones activas globales (Collection Group con fallback por usuario)
    useEffect(() => {
        if (activeMainTab !== 'sesiones_activas') return;

        setSessionsLoading(true);
        let unsubscribeGroup = null;
        let unsubscribesPerUser = [];

        const loadFallbackPerUser = async () => {
            try {
                console.log('[ControlSesiones] Activando fallback per-usuario para sesiones activas...');
                const usuariosSnap = await getDocs(collection(db, 'usuarios'));
                const newUsersMap = { ...usersMap };
                usuariosSnap.docs.forEach(docSnap => {
                    newUsersMap[docSnap.id] = docSnap.data();
                });
                setUsersMap(newUsersMap);

                const userDocs = usuariosSnap.docs;
                const combinedSessionsMap = {};

                unsubscribesPerUser.forEach(unsub => unsub());
                unsubscribesPerUser = [];

                userDocs.forEach(userDoc => {
                    const uid = userDoc.id;
                    const sCollRef = collection(db, 'usuarios', uid, 'sesiones_activas');
                    const uUnsub = onSnapshot(sCollRef, (snap) => {
                        const userSessions = snap.docs.map(d => ({
                            id: d.id,
                            userId: uid,
                            ...d.data()
                        }));
                        combinedSessionsMap[uid] = userSessions;

                        const flattened = Object.values(combinedSessionsMap).flat();
                        flattened.sort((a, b) => {
                            const tA = a.ultimoAcceso?.toDate ? a.ultimoAcceso.toDate().getTime() : (typeof a.ultimoAcceso === 'number' ? a.ultimoAcceso : 0);
                            const tB = b.ultimoAcceso?.toDate ? b.ultimoAcceso.toDate().getTime() : (typeof b.ultimoAcceso === 'number' ? b.ultimoAcceso : 0);
                            return tB - tA;
                        });

                        setAllActiveSessions(flattened);
                        setSessionsLoading(false);
                    }, (err) => {
                        console.warn(`[ControlSesiones] Fallback read warning for user ${uid}:`, err);
                    });
                    unsubscribesPerUser.push(uUnsub);
                });
            } catch (fallbackErr) {
                console.error('[ControlSesiones] Fallback per-user failed:', fallbackErr);
                setSessionsLoading(false);
            }
        };

        try {
            const q = collectionGroup(db, 'sesiones_activas');
            unsubscribeGroup = onSnapshot(q, async (snapshot) => {
                const list = snapshot.docs.map((docSnap) => {
                    const data = docSnap.data();
                    const userId = docSnap.ref.parent?.parent?.id || data.userId || null;
                    return {
                        id: docSnap.id,
                        userId,
                        ...data,
                    };
                });

                const missingUids = new Set();
                list.forEach((item) => {
                    if (item.userId && !usersMap[item.userId]) {
                        missingUids.add(item.userId);
                    }
                });

                if (missingUids.size > 0) {
                    const newUsersMap = { ...usersMap };
                    const promises = Array.from(missingUids).map(async (uid) => {
                        try {
                            const uDoc = await getDoc(doc(db, 'usuarios', uid));
                            if (uDoc.exists()) {
                                newUsersMap[uid] = uDoc.data();
                            }
                        } catch (e) {
                            console.warn(`[ControlSesiones] Error fetching user ${uid}:`, e);
                        }
                    });
                    await Promise.all(promises);
                    setUsersMap(newUsersMap);
                }

                list.sort((a, b) => {
                    const tA = a.ultimoAcceso?.toDate ? a.ultimoAcceso.toDate().getTime() : (typeof a.ultimoAcceso === 'number' ? a.ultimoAcceso : 0);
                    const tB = b.ultimoAcceso?.toDate ? b.ultimoAcceso.toDate().getTime() : (typeof b.ultimoAcceso === 'number' ? b.ultimoAcceso : 0);
                    return tB - tA;
                });

                setAllActiveSessions(list);
                setSessionsLoading(false);
            }, (err) => {
                console.warn('[ControlSesiones] Listener de collectionGroup fallo o sin permisos, iniciando fallback por usuario...', err);
                loadFallbackPerUser();
            });
        } catch (e) {
            console.warn('[ControlSesiones] Error al iniciar collectionGroup, usando fallback por usuario:', e);
            loadFallbackPerUser();
        }

        return () => {
            if (unsubscribeGroup) unsubscribeGroup();
            unsubscribesPerUser.forEach(unsub => unsub());
        };
    }, [activeMainTab]);

    const kpiMetrics = useMemo(() => {
        const totalSessions = allActiveSessions.length;
        const uniqueUsers = new Set(allActiveSessions.map(s => s.userId).filter(Boolean)).size;
        let mobileCount = 0;
        let desktopCount = 0;
        let onlineCount = 0;
        const now = Date.now();

        allActiveSessions.forEach((s) => {
            if (isMobileDevice(s.userAgent)) {
                mobileCount++;
            } else {
                desktopCount++;
            }
            const time = s.ultimoAcceso?.toDate ? s.ultimoAcceso.toDate().getTime() : 0;
            if (time > 0 && (now - time) <= 5 * 60 * 1000) {
                onlineCount++;
            }
        });

        return { totalSessions, uniqueUsers, mobileCount, desktopCount, onlineCount };
    }, [allActiveSessions]);

    const filteredSessions = useMemo(() => {
        return allActiveSessions.filter((s) => {
            const uData = usersMap[s.userId] || {};
            const nombre = (s.nombre || uData.nombre || uData.displayName || '').toLowerCase();
            const email = (s.email || uData.email || '').toLowerCase();
            const rol = (s.rol || uData.rol || '').toLowerCase();
            const fraccId = s.fraccionamientoId || uData.fraccionamientoId || '';
            const fraccNombre = (fraccionamientosMap[fraccId] || '').toLowerCase();
            const search = sessionSearchTerm.toLowerCase().trim();

            if (search) {
                const matchName = nombre.includes(search);
                const matchEmail = email.includes(search);
                const matchFracc = fraccNombre.includes(search);
                if (!matchName && !matchEmail && !matchFracc) return false;
            }

            if (sessionRoleFilter !== 'todos') {
                if (sessionRoleFilter === 'platformadmin' && !['platformadmin', 'plataforma'].includes(rol)) return false;
                if (sessionRoleFilter === 'superadmin' && !['superadmin', 'admin'].includes(rol)) return false;
                if (sessionRoleFilter === 'residente' && !['user', 'residente', 'habitante', 'propietario', 'inquilino'].includes(rol)) return false;
                if (sessionRoleFilter === 'caseta' && !['caseta', 'vigilante'].includes(rol)) return false;
            }

            if (sessionDeviceFilter !== 'todos') {
                const isMobile = isMobileDevice(s.userAgent);
                if (sessionDeviceFilter === 'escritorio' && isMobile) return false;
                if (sessionDeviceFilter === 'movil' && !isMobile) return false;
            }

            return true;
        });
    }, [allActiveSessions, usersMap, fraccionamientosMap, sessionSearchTerm, sessionRoleFilter, sessionDeviceFilter]);

    const paginatedSessions = useMemo(() => {
        const indexOfLastSession = currentPage * sessionsPerPage;
        const indexOfFirstSession = indexOfLastSession - sessionsPerPage;
        return filteredSessions.slice(indexOfFirstSession, indexOfLastSession);
    }, [filteredSessions, currentPage]);

    const totalPages = Math.ceil(filteredSessions.length / sessionsPerPage);

    const renderPagination = () => {
        if (totalPages <= 1) return null;

        const maxPageButtons = 5;
        let startPage = Math.max(1, currentPage - Math.floor(maxPageButtons / 2));
        let endPage = Math.min(totalPages, startPage + maxPageButtons - 1);

        if (endPage - startPage + 1 < maxPageButtons) {
            startPage = Math.max(1, endPage - maxPageButtons + 1);
        }

        const pageNumbers = [];
        for (let i = startPage; i <= endPage; i++) {
            pageNumbers.push(i);
        }

        return (
            <div className="zen-pagination">
                <button
                    type="button"
                    className="pagination-arrow"
                    onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                    disabled={currentPage === 1}
                >
                    &laquo; Anterior
                </button>

                {startPage > 1 && (
                    <>
                        <button
                            type="button"
                            className={`pagination-number ${currentPage === 1 ? 'active' : ''}`}
                            onClick={() => setCurrentPage(1)}
                        >
                            1
                        </button>
                        {startPage > 2 && <span className="pagination-ellipsis">...</span>}
                    </>
                )}

                {pageNumbers.map((number) => (
                    <button
                        key={number}
                        type="button"
                        className={`pagination-number ${currentPage === number ? 'active' : ''}`}
                        onClick={() => setCurrentPage(number)}
                    >
                        {number}
                    </button>
                ))}

                {endPage < totalPages && (
                    <>
                        {endPage < totalPages - 1 && <span className="pagination-ellipsis">...</span>}
                        <button
                            type="button"
                            className={`pagination-number ${currentPage === totalPages ? 'active' : ''}`}
                            onClick={() => setCurrentPage(totalPages)}
                        >
                            {totalPages}
                        </button>
                    </>
                )}

                <button
                    type="button"
                    className="pagination-arrow"
                    onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
                    disabled={currentPage === totalPages}
                >
                    Siguiente &raquo;
                </button>
            </div>
        );
    };

    const handleRevocarSesionRemota = async (sessionItem) => {
        const uData = usersMap[sessionItem.userId] || {};
        const userName = sessionItem.nombre || uData.nombre || sessionItem.email || 'el usuario';
        const deviceName = `${parseBrowserName(sessionItem.userAgent)} en ${parseOS(sessionItem.userAgent)}`;

        const result = await Swal.fire({
            title: '¿Revocar sesión remota?',
            html: `Se desconectará inmediatamente a <strong>${userName}</strong> del dispositivo:<br/><br/><code style="color: #6366f1;">${deviceName}</code>`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#ef4444',
            cancelButtonColor: '#64748b',
            confirmButtonText: 'Sí, revocar sesión',
            cancelButtonText: 'Cancelar'
        });

        if (result.isConfirmed) {
            try {
                if (sessionItem.userId && sessionItem.id) {
                    await deleteDoc(doc(db, 'usuarios', sessionItem.userId, 'sesiones_activas', sessionItem.id));
                    Swal.fire({
                        title: 'Sesión Terminada',
                        text: `La sesión remota de ${userName} fue revocada exitosamente.`,
                        icon: 'success',
                        timer: 2000,
                        showConfirmButton: false
                    });
                }
            } catch (err) {
                console.error('[ControlSesiones] Error al revocar sesión remota:', err);
                Swal.fire('Error', 'No se pudo revocar la sesión. Intenta nuevamente.', 'error');
            }
        }
    };

    const resolveAuthDate = (usuario) => {
        const ts = usuario.fechaCreacion || usuario.createdAt || usuario.creadoEn || usuario.fechaRegistro;
        if (ts?.toDate) return ts.toDate();
        if (typeof ts === 'number') return new Date(ts);
        if (typeof ts === 'string') {
            const d = new Date(ts);
            return Number.isNaN(d.getTime()) ? null : d;
        }
        return null;
    };

    const formatDateTime = (date) => {
        if (!date) return '—';
        return date.toLocaleString('es-MX', { dateStyle: 'medium', timeStyle: 'short' });
    };

    const openEditModal = (usuario) => {
        setEditingUser(usuario);
        setEditForm({
            nombre: usuario.nombre || usuario.displayName || usuario.nombreUsuario || '',
            correo: usuario.correo || usuario.email || '',
            usuario: usuario.nombreUsuario || usuario.usuario || usuario.email || '',
            telefono: usuario.telefono || usuario.celular || usuario.phone || '',
            rol: usuario.rol || '',
            nuevaContrasena: '',
            confirmContrasena: '',
        });
        setPasswordError('');
        setActiveTab('perfil');
        const cf = usuario.customFields || usuario.camposPersonalizados || [];
        if (Array.isArray(cf)) {
            setCustomFields(cf.map((f) => ({ name: f.name || f.nombre || '', value: f.value || f.valor || '' })));
        } else if (cf && typeof cf === 'object') {
            setCustomFields(Object.entries(cf).map(([name, value]) => ({ name, value })));
        } else {
            setCustomFields([]);
        }
    };

    const closeEditModal = () => {
        setEditingUser(null);
        setSaving(false);
    };

    const handleEditChange = (e) => {
        const { name, value } = e.target;

        if (name === 'rol' && value === 'platformadmin') {
            setPendingRole(value);
            setShowPlatformAdminConfirm(true);
        } else {
            setEditForm((prev) => ({ ...prev, [name]: value }));
        }
    };

    const handleConfirmPlatformAdmin = () => {
        setEditForm((prev) => ({ ...prev, rol: pendingRole }));
        setShowPlatformAdminConfirm(false);
        setPendingRole('');
    };

    const handleCancelPlatformAdmin = () => {
        setShowPlatformAdminConfirm(false);
        setPendingRole('');
    };

    const handleSave = async () => {
        if (!editingUser) return;
        setSaving(true);
        try {
            if (editForm.nuevaContrasena.trim()) {
                if ((editForm.confirmContrasena || '').trim() !== editForm.nuevaContrasena.trim()) {
                    setPasswordError('Las contraseñas no coinciden.');
                    setSaving(false);
                    setActiveTab('perfil');
                    return;
                } else {
                    setPasswordError('');
                }
            }
            const ref = doc(db, 'usuarios', editingUser.id);
            const updateData = {
                nombre: editForm.nombre,
                displayName: editForm.nombre,
                correo: editForm.correo,
                email: editForm.correo,
                nombreUsuario: editForm.usuario,
                usuario: editForm.usuario,
                telefono: editForm.telefono,
                celular: editForm.telefono,
                phone: editForm.telefono,
                rol: editForm.rol,
                customFields: Object.fromEntries(
                    (customFields || [])
                        .filter((f) => (f.name || '').trim().length > 0)
                        .map((f) => [f.name.trim(), (f.value ?? '').toString()])
                ),
            };

            if (editForm.nuevaContrasena.trim()) {
                const targetUid = editingUser.uid || editingUser.id;
                const targetEmail = (editForm.correo || '').trim().toLowerCase();
                const payload = {
                    uid: targetUid,
                    email: targetEmail,
                    newPassword: editForm.nuevaContrasena,
                    rol: editForm.rol,
                };

                try {
                    const setUserPassword = httpsCallable(functions, 'setUserPassword');
                    await setUserPassword(payload);
                    console.log('Contraseña asignada correctamente (callable)');
                } catch (error) {
                    console.error('Error asignando contraseña (callable):', error);
                    try {
                        const response = await fetch(PASSWORD_ENDPOINT, {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify(payload),
                        });
                        const result = await response.json();

                        if (!response.ok || !result?.success) {
                            throw new Error(result?.message || 'Fallo en fallback HTTP');
                        }
                        console.log('Contraseña asignada correctamente (fallback HTTP)');
                    } catch (httpError) {
                        console.error('Fallback HTTP setUserPasswordHttp falló:', httpError);
                        setSaving(false);
                        alert('No se pudo asignar la contraseña. Verifica que el usuario exista en Authentication y reintenta.');
                        return;
                    }
                }
            }

            await updateDoc(ref, updateData);

            setUsuariosGlobales((prev) => prev.map((u) => (u.id === editingUser.id ? { ...u, ...editForm, nuevaContrasena: '', confirmContrasena: '', customFields: updateData.customFields } : u)));
            closeEditModal();
        } catch (error) {
            console.error('Error actualizando usuario', error);
            setSaving(false);
        }
    };

    const handleSendReset = async (usuario) => {
        const correo = usuario.correo || usuario.email;
        if (!correo) {
            setResetFeedback({
                email: 'Correo no disponible',
                error: true,
                message: 'El usuario no tiene un correo válido en su perfil.',
            });
            return;
        }
        try {
            setSendingReset(usuario.id);
            await sendPasswordResetEmail(auth, correo);
            setResetFeedback({
                email: correo,
                error: false,
                message: 'Enviamos el enlace de restablecimiento a:',
            });
        } catch (error) {
            console.error('No se pudo enviar el correo de restablecimiento', error);
            setResetFeedback({
                email: correo,
                error: true,
                message: 'No se pudo enviar el correo. Verifica que el correo exista en Auth.',
            });
        } finally {
            setSendingReset('');
        }
    };

    const handleToggleSuspend = async (usuario) => {
        try {
            // Asegurar que el usuario esté autenticado antes de proceder
            if (!auth.currentUser) {
                throw new Error('No hay una sesión activa de administrador.');
            }

            // Forzar actualización del token para asegurar que el backend reciba las credenciales frescas
            const token = await auth.currentUser.getIdToken(true);

            const toggle = !!usuario.suspendido ? false : true;
            const data = { uid: usuario.id, suspend: toggle };

            console.log(`[ControlSesiones] Intentando suspensión para UID: ${usuario.id}, Suspend: ${toggle}`);

            try {
                const suspendUser = httpsCallable(functions, 'suspendUser');
                const res = await suspendUser(data);

                if (res.data && res.data.success === false) {
                    throw new Error(res.data.message || 'Error en el servidor.');
                }
            } catch (callableErr) {
                console.warn('Fallo callable suspendUser en ControlSesiones, intentando fallback HTTP...', callableErr);

                const response = await fetch(SUSPEND_ENDPOINT, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`
                    },
                    body: JSON.stringify(data),
                });

                const result = await response.json();
                if (!response.ok || !result?.success) {
                    throw new Error(result?.message || 'Error en el servidor (Fallback HTTP).');
                }
                console.log('Suspensión completada vía Fallback HTTP');
            }

            setUsuariosGlobales((prev) => prev.map((u) => (u.id === usuario.id ? { ...u, suspendido: toggle } : u)));

            // Mostrar modal de éxito
            setConfirmModal({
                isOpen: true,
                title: 'Operación exitosa',
                message: `Usuario ${toggle ? 'suspendido' : 'habilitado'} correctamente.`,
                type: 'success',
                showCancel: false,
                confirmText: 'Entendido',
                onConfirm: () => setConfirmModal({ ...confirmModal, isOpen: false }),
            });
        } catch (err) {
            console.error('Error definitivo en suspensión (ControlSesiones):', err);
            const detailedMsg = err.message || 'No se pudo actualizar la suspensión. Intenta nuevamente.';

            setConfirmModal({
                isOpen: true,
                title: 'Error de suspensión',
                message: detailedMsg,
                type: 'warning',
                showCancel: false,
                confirmText: 'Cerrar',
                onConfirm: () => setConfirmModal({ ...confirmModal, isOpen: false }),
            });
        }
    };

    const adminsFiltered = useMemo(() => {
        return usuariosGlobales
            .filter((u) => (u.rol || '').toLowerCase() === 'platformadmin')
            .filter((usuario) => {
                if (!searchTerm) return true;
                const term = searchTerm.toLowerCase();
                const nombre = (usuario.nombre || usuario.displayName || usuario.nombreUsuario || '').toLowerCase();
                const correo = (usuario.correo || usuario.email || '').toLowerCase();
                const usuarioLogin = (usuario.nombreUsuario || usuario.email || '').toLowerCase();
                return nombre.includes(term) || correo.includes(term) || usuarioLogin.includes(term);
            })
            .sort((a, b) => {
                const dateA = resolveAuthDate(a) || new Date(0);
                const dateB = resolveAuthDate(b) || new Date(0);
                return dateB - dateA;
            });
    }, [usuariosGlobales, searchTerm]);

    const renderTable = (usuarios) => {
        if (usuarios.length === 0) {
            return (
                <div className="empty-results-box">
                    <p>No se encontraron administradores de plataforma con los criterios de búsqueda.</p>
                </div>
            );
        }

        return (
            <div className="gestion-sesiones-table-container">
                <table className="gestion-sesiones-table">
                    <thead>
                        <tr>
                            <th>Nombre</th>
                            <th>Correo / Usuario</th>
                            <th>Teléfono</th>
                            <th>Rol</th>
                            <th>Última Actividad</th>
                            <th className="actions-header">Acciones</th>
                        </tr>
                    </thead>
                    <tbody>
                        {usuarios.map((usuario) => {
                            const nombre = usuario.nombre || usuario.displayName || usuario.nombreUsuario || '—';
                            const correo = usuario.correo || usuario.email || '—';
                            const usuarioLogin = usuario.nombreUsuario || usuario.email || '—';
                            const telefono = usuario.telefono || usuario.celular || usuario.phone || '—';
                            const rol = usuario.rol || '—';
                            const authDate = formatDateTime(resolveAuthDate(usuario));
                            return (
                                <tr key={usuario.id}>
                                    <td className="user-name-cell">{nombre}</td>
                                    <td>
                                        <div className="user-email-stack">
                                            <span className="main-email">{correo}</span>
                                            {usuarioLogin !== correo && <span className="sub-user">{usuarioLogin}</span>}
                                        </div>
                                    </td>
                                    <td>{telefono}</td>
                                    <td>
                                        <span className="role-badge platformadmin">Platform Admin</span>
                                    </td>
                                    <td className="date-cell">{authDate}</td>
                                    <td>
                                        <div className="acciones-row">
                                            {correo && correo !== '—' && (
                                                <button
                                                    type="button"
                                                    className="action-icon-btn key"
                                                    title="Resetear contraseña"
                                                    onClick={() => handleSendReset(usuario)}
                                                    disabled={sendingReset === usuario.id}
                                                >
                                                    <FiKey />
                                                </button>
                                            )}
                                            <button
                                                type="button"
                                                className="action-icon-btn edit"
                                                title="Editar perfil"
                                                onClick={() => openEditModal(usuario)}
                                            >
                                                <FiEdit2 />
                                            </button>
                                            <button
                                                type="button"
                                                className={`action-icon-btn ${usuario.suspendido ? 'unlock' : 'lock'}`}
                                                title={usuario.suspendido ? 'Habilitar usuario' : 'Suspender usuario'}
                                                onClick={() => handleToggleSuspend(usuario)}
                                            >
                                                {usuario.suspendido ? <FiUnlock /> : <FiLock />}
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        );
    };

    return (
        <div className="gestion-usuarios-container">
            {resetFeedback && (
                <div className="reset-modal-backdrop">
                    <div className={`reset-modal ${resetFeedback.error ? 'error' : 'success'}`}>
                        <div className="reset-modal-header">
                            {resetFeedback.error ? (
                                <FiAlertTriangle className="reset-modal-icon error" />
                            ) : (
                                <FiCheckCircle className="reset-modal-icon success" />
                            )}
                            <div>
                                <p className="reset-modal-title">{resetFeedback.error ? 'No se pudo enviar' : 'Correo de restablecimiento enviado'}</p>
                                <p className="reset-modal-subtitle">{resetFeedback.message}</p>
                            </div>
                        </div>
                        <div className="reset-modal-body">
                            <div className="reset-modal-email">{resetFeedback.email || 'Correo no disponible'}</div>
                            {resetFeedback.error ? (
                                <p className="reset-modal-hint">Confirma que el usuario existe en Auth y que el correo sea correcto.</p>
                            ) : (
                                <p className="reset-modal-hint">Pide al usuario revisar bandeja de entrada o spam.</p>
                            )}
                        </div>
                        <div className="reset-modal-footer">
                            <button className="reset-modal-btn" onClick={() => setResetFeedback(null)}>Aceptar</button>
                        </div>
                    </div>
                </div>
            )}
            <div className="gestion-usuarios-header">
                <PlatformBackButton />
                <div className="gestion-usuarios-title">
                    <FaUserShield className="gestion-usuarios-icon" />
                    <div>
                        <h1>Control de Sesiones - Usuarios Globales</h1>
                        <p>Gestiona usuarios sin fraccionamiento asignado y monitorea las sesiones activas globales.</p>
                    </div>
                </div>
            </div>

            {/* BARRA DE PESTAÑAS PRINCIPAL */}
            <div className="control-sesiones-tabs">
                <button
                    type="button"
                    className={`control-tab-btn ${activeMainTab === 'usuarios_globales' ? 'active' : ''}`}
                    onClick={() => setActiveMainTab('usuarios_globales')}
                >
                    <FaUsers className="tab-icon" />
                    <span>Usuarios Sin Fraccionamiento</span>
                    <span className="tab-count-pill">{adminsFiltered.length}</span>
                </button>
                <button
                    type="button"
                    className={`control-tab-btn ${activeMainTab === 'sesiones_activas' ? 'active' : ''}`}
                    onClick={() => setActiveMainTab('sesiones_activas')}
                >
                    <FaShieldAlt className="tab-icon" />
                    <span>Dispositivos y Sesiones Activas Globales</span>
                    <span className="tab-count-pill highlight">{allActiveSessions.length}</span>
                </button>
                <button
                    type="button"
                    className={`control-tab-btn ${activeMainTab === 'configuracion_app' ? 'active' : ''}`}
                    onClick={() => setActiveMainTab('configuracion_app')}
                >
                    <FaTools className="tab-icon" />
                    <span>Configuración de Sistema</span>
                </button>
                <button
                    type="button"
                    className={`control-tab-btn ${activeMainTab === 'analitica_ejecutiva' ? 'active' : ''}`}
                    onClick={() => setActiveMainTab('analitica_ejecutiva')}
                >
                    <FaChartBar className="tab-icon" />
                    <span>Analítica y Reportes Ejecutivos</span>
                </button>
            </div>

            {/* CONTENIDO PESTAÑA 1: USUARIOS SIN FRACCIONAMIENTO */}
            {activeMainTab === 'usuarios_globales' && (
                <div className="gestion-sesiones-card">
                    <div className="toolbar-section">
                        <div className="search-box-v2">
                            <FaSearch className="inner-search-icon" />
                            <input
                                type="text"
                                placeholder="Buscar por nombre, correo o usuario..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>
                    </div>

                    <div className="gestion-sesiones-body">
                        {usuariosLoading && <div className="loading-state">Cargando administradores…</div>}
                        {usuariosError && <div className="error-state">{usuariosError}</div>}
                        {!usuariosLoading && !usuariosError && (
                            renderTable(adminsFiltered)
                        )}
                    </div>
                </div>
            )}

            {/* CONTENIDO PESTAÑA 2: DISPOSITIVOS Y SESIONES ACTIVAS GLOBALES */}
            {activeMainTab === 'sesiones_activas' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                    {/* TARJETAS DE KPIS Y MÉTRICAS */}
                    <div className="sessions-kpis-grid">
                        <div className="kpi-card-session">
                            <div className="kpi-icon-wrapper purple">
                                <FaShieldAlt />
                            </div>
                            <div className="kpi-info-content">
                                <h4>Sesiones Activas</h4>
                                <p className="kpi-number-val">{kpiMetrics.totalSessions}</p>
                            </div>
                        </div>

                        <div className="kpi-card-session">
                            <div className="kpi-icon-wrapper blue">
                                <FaUserTie />
                            </div>
                            <div className="kpi-info-content">
                                <h4>Usuarios Conectados</h4>
                                <p className="kpi-number-val">{kpiMetrics.uniqueUsers}</p>
                            </div>
                        </div>

                        <div className="kpi-card-session">
                            <div className="kpi-icon-wrapper emerald">
                                <FaMobileAlt />
                            </div>
                            <div className="kpi-info-content">
                                <h4>Móviles vs Escritorio</h4>
                                <p className="kpi-number-val">{kpiMetrics.mobileCount} / {kpiMetrics.desktopCount}</p>
                            </div>
                        </div>

                        <div className="kpi-card-session">
                            <div className="kpi-icon-wrapper amber">
                                <FaCircle style={{ color: '#10b981', fontSize: '1.1rem' }} />
                            </div>
                            <div className="kpi-info-content">
                                <h4>En Línea (&lt; 5 min)</h4>
                                <p className="kpi-number-val">{kpiMetrics.onlineCount}</p>
                            </div>
                        </div>

                        <div className="kpi-card-session">
                            <div className="kpi-icon-wrapper" style={{ background: '#f0fdfa', color: '#0d9488' }}>
                                <FaUserShield />
                            </div>
                            <div className="kpi-info-content">
                                <h4>Aviso de Privacidad</h4>
                                <p className="kpi-number-val">
                                    {privacyStats.accepted} <span style={{ fontSize: '0.85rem', fontWeight: 600, color: '#64748b' }}>/ {privacyStats.total}</span>
                                </p>
                                <span style={{ fontSize: '0.75rem', color: privacyStats.pending > 0 ? '#d97706' : '#10b981', fontWeight: 600 }}>
                                    {privacyStats.percentage}% aceptado ({privacyStats.pending} pend.)
                                </span>
                            </div>
                        </div>
                    </div>

                    {/* TARJETA PRINCIPAL CON CONTROLES Y LISTA */}
                    <div className="gestion-sesiones-card">
                        <div className="sessions-toolbar-section">
                            <div className="search-box-v2" style={{ maxWidth: '380px' }}>
                                <FaSearch className="inner-search-icon" />
                                <input
                                    type="text"
                                    placeholder="Buscar por usuario, correo o fraccionamiento..."
                                    value={sessionSearchTerm}
                                    onChange={(e) => setSessionSearchTerm(e.target.value)}
                                />
                            </div>

                            <div className="sessions-filters-group">
                                <select
                                    className="session-filter-select"
                                    value={sessionRoleFilter}
                                    onChange={(e) => setSessionRoleFilter(e.target.value)}
                                >
                                    <option value="todos">Todos los Roles</option>
                                    <option value="platformadmin">Platform Admin (Gerente)</option>
                                    <option value="superadmin">Super Admin (Fraccionamiento)</option>
                                    <option value="residente">Residente / Propietario</option>
                                    <option value="caseta">Caseta / Vigilancia</option>
                                </select>

                                <select
                                    className="session-filter-select"
                                    value={sessionDeviceFilter}
                                    onChange={(e) => setSessionDeviceFilter(e.target.value)}
                                >
                                    <option value="todos">Todos los Dispositivos</option>
                                    <option value="escritorio">Solo Computadoras (Desktop)</option>
                                    <option value="movil">Solo Móviles / Tablets</option>
                                </select>
                            </div>
                        </div>

                        {/* CONTENIDO DE LISTA DE SESIONES ACTIVAS */}
                        <div className="gestion-sesiones-body">
                            {sessionsLoading ? (
                                <div className="loading-state">Monitoreando sesiones en tiempo real…</div>
                            ) : filteredSessions.length === 0 ? (
                                <div className="sessions-empty-state">
                                    <FaShieldAlt style={{ fontSize: '2.5rem', color: '#cbd5e1' }} />
                                    <h3>No se encontraron sesiones activas</h3>
                                    <p>Intenta ajustar el término de búsqueda o los filtros seleccionados.</p>
                                </div>
                            ) : (
                                <>
                                    <div className="sessions-cards-container">
                                        {paginatedSessions.map((sessionItem) => {
                                            const uData = usersMap[sessionItem.userId] || {};
                                            const nombre = sessionItem.nombre || uData.nombre || uData.displayName || 'Usuario de Plataforma';
                                            const email = sessionItem.email || uData.email || 'Correo no disponible';
                                            const rolRaw = (sessionItem.rol || uData.rol || 'user').toLowerCase();
                                            const fraccId = sessionItem.fraccionamientoId || uData.fraccionamientoId || null;
                                            const fraccNombre = fraccId ? (fraccionamientosMap[fraccId] || `Fraccionamiento (${fraccId.slice(0, 6)}...)`) : 'Acceso Global / Plataforma';

                                            const browserName = parseBrowserName(sessionItem.userAgent);
                                            const osName = parseOS(sessionItem.userAgent);
                                            const isMobile = isMobileDevice(sessionItem.userAgent);
                                            const lastAccessTime = sessionItem.ultimoAcceso?.toDate ? sessionItem.ultimoAcceso.toDate().getTime() : 0;
                                            const isOnlineRecently = lastAccessTime > 0 && (Date.now() - lastAccessTime) <= 5 * 60 * 1000;

                                            let roleClass = 'residente';
                                            let roleLabel = 'Residente';
                                            if (['platformadmin', 'plataforma'].includes(rolRaw)) {
                                                roleClass = 'platformadmin';
                                                roleLabel = 'Platform Admin';
                                            } else if (['superadmin', 'admin'].includes(rolRaw)) {
                                                roleClass = 'superadmin';
                                                roleLabel = 'Super Admin';
                                            } else if (['caseta', 'vigilante'].includes(rolRaw)) {
                                                roleClass = 'caseta';
                                                roleLabel = 'Caseta';
                                            }

                                            return (
                                                <div className="session-item-card" key={sessionItem.id}>
                                                    <div className="session-device-visual">
                                                        {isMobile ? <FaMobileAlt /> : <FaLaptop />}
                                                        <div className={`session-status-pulse ${isOnlineRecently ? 'online' : 'idle'}`} title={isOnlineRecently ? 'Sesión activa en línea (< 5 min)' : 'Sesión reciente'} />
                                                    </div>

                                                    <div className="session-main-details">
                                                        <div className="session-user-line">
                                                            <span className="session-user-name">{nombre}</span>
                                                            <span className="session-user-email">({email})</span>
                                                            <span className={`session-role-badge ${roleClass}`}>{roleLabel}</span>
                                                        </div>

                                                        <div className="session-device-line">
                                                            <span>
                                                                <strong>Navegador / Dispositivo:</strong> {browserName} en {osName}
                                                            </span>
                                                            <span>•</span>
                                                            <span className="session-fracc-tag">
                                                                <FaBuilding style={{ color: '#6366f1' }} />
                                                                {fraccNombre}
                                                            </span>
                                                            <span>•</span>
                                                            <span>
                                                                <FaClock style={{ color: '#94a3b8' }} />
                                                                Último acceso: {formatFechaCST(sessionItem.ultimoAcceso)}
                                                            </span>
                                                        </div>
                                                    </div>

                                                    <div className="session-action-area">
                                                        <button
                                                            type="button"
                                                            className="btn-revocar-remoto"
                                                            onClick={() => handleRevocarSesionRemota(sessionItem)}
                                                            title="Revocar y cerrar esta sesión inmediatamente"
                                                        >
                                                            <FaTrashAlt />
                                                            <span>Cerrar sesión</span>
                                                        </button>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                    {renderPagination()}
                                </>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {activeMainTab === 'configuracion_app' && (
                <div className="config-app-grid">
                    {/* COLUMNA IZQUIERDA: FORMULARIO DE CONTROL */}
                    <div className="config-form-section">
                        <h3 className="config-section-title">
                            <FaTools style={{ color: '#4f46e5' }} />
                            Control de Despliegue de la Aplicación
                        </h3>
                        <p className="config-section-desc">
                            Administra los parámetros de versión obligatoria y mensajes de actualización para la aplicación móvil nativa.
                        </p>

                        {configLoading ? (
                            <div className="loading-state">Cargando configuración actual...</div>
                        ) : (
                            <form onSubmit={handleSaveConfig}>
                                <div className="config-form-group">
                                    <label htmlFor="current_version">Versión Activa en la Tienda</label>
                                    <input
                                        id="current_version"
                                        type="text"
                                        className="config-input-text"
                                        value={appConfig.current_version}
                                        onChange={(e) => setAppConfig({ ...appConfig, current_version: e.target.value })}
                                        placeholder="Ej: 1.0.8"
                                        required
                                    />
                                </div>

                                <div className="config-form-group">
                                    <label htmlFor="play_store_url">Enlace de la App en Google Play Store</label>
                                    <input
                                        id="play_store_url"
                                        type="url"
                                        className="config-input-text"
                                        value={appConfig.play_store_url}
                                        onChange={(e) => setAppConfig({ ...appConfig, play_store_url: e.target.value })}
                                        placeholder="https://play.google.com/store/apps/details?id=..."
                                        required
                                    />
                                </div>

                                <div className="toggle-switch-wrapper">
                                    <div className="toggle-switch-info">
                                        <span className="toggle-switch-title">Actualización Obligatoria (Force Update)</span>
                                        <span className="toggle-switch-desc">
                                            Fuerza al cliente móvil a actualizar si la versión local es diferente de la versión activa.
                                        </span>
                                    </div>
                                    <label className="switch-control">
                                        <input
                                            type="checkbox"
                                            checked={appConfig.force_update_enabled}
                                            onChange={(e) => setAppConfig({ ...appConfig, force_update_enabled: e.target.checked })}
                                        />
                                        <span className="slider-switch"></span>
                                    </label>
                                </div>

                                <div className="config-form-group">
                                    <label htmlFor="update_message">Novedades y Mejoras de la Versión</label>
                                    <textarea
                                        id="update_message"
                                        className="config-textarea"
                                        value={appConfig.update_message}
                                        onChange={(e) => setAppConfig({ ...appConfig, update_message: e.target.value })}
                                        placeholder="Describe brevemente las mejoras en esta actualización..."
                                        required
                                    />
                                </div>

                                <button type="submit" className="config-submit-btn" disabled={configSaving}>
                                    {configSaving ? 'Guardando cambios...' : 'Guardar Configuración de Sistema'}
                                </button>
                            </form>
                        )}
                    </div>

                    {/* COLUMNA DERECHA: CONSOLA DE GOOGLE PLAY & TRACKS */}
                    <div className="config-play-console-section">
                        <h3 className="config-section-title">
                            <FaGooglePlay style={{ color: '#60a5fa' }} />
                            Versiones en Google Play Console
                        </h3>
                        <p className="config-section-desc">
                            Historial de compilaciones y pistas de distribución asociadas al desarrollador.
                        </p>

                        <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', marginBottom: '1.25rem', alignItems: 'center' }}>
                            <div className="play-console-account-badge" style={{ marginBottom: 0 }}>
                                <FaGooglePlay />
                                Cuenta vinculada: domushabitat2025@gmail.com
                            </div>
                            <button
                                type="button"
                                className="btn-sync-playstore"
                                onClick={handleSyncPlayStore}
                                disabled={syncingPlayStore}
                            >
                                {syncingPlayStore ? 'Sincronizando...' : 'Sincronizar en Tiempo Real'}
                            </button>
                        </div>

                        <div className="play-console-api-alert">
                            <span className="play-console-api-alert-title">
                                ℹ️ Información de Sincronización
                            </span>
                            <p className="play-console-api-alert-text">
                                El acceso automatizado a pistas privadas (Prueba Interna, Beta y Lanzamientos) requiere configurar una Cuenta de Servicio con la API Google Play Developer en la consola de Google Play de <strong>domushabitat2025@gmail.com</strong>.
                            </p>
                        </div>

                        {/* PISTA DE PRODUCCIÓN */}
                        <div className="track-container">
                            <div className="track-header">
                                <span className="track-title">Canal de Producción</span>
                                <button
                                    type="button"
                                    className="btn-add-track-version"
                                    onClick={() => handleAddTrackVersion('produccion')}
                                >
                                    + Agregar Compilación
                                </button>
                            </div>

                            <div className="track-versions-list">
                                {appConfig.google_play_tracks.produccion?.length === 0 ? (
                                    <p style={{ fontStyle: 'italic', fontSize: '0.85rem', color: '#94a3b8', margin: '0 0 1rem 0' }}>
                                        No hay compilaciones registradas en Producción.
                                    </p>
                                ) : (
                                    appConfig.google_play_tracks.produccion?.map((version, index) => (
                                        <div className="track-version-card-edit" key={`prod-${index}`}>
                                            <input
                                                type="text"
                                                className="track-input-short"
                                                value={version.version_code}
                                                onChange={(e) => handleTrackFieldChange('produccion', index, 'version_code', e.target.value)}
                                                placeholder="Code"
                                                title="Código de compilación (versionCode)"
                                            />
                                            <input
                                                type="text"
                                                className="track-input-med"
                                                value={version.version_name}
                                                onChange={(e) => handleTrackFieldChange('produccion', index, 'version_name', e.target.value)}
                                                placeholder="Name"
                                                title="Versión comercial (versionName)"
                                            />
                                            <select
                                                className={`track-select-status ${
                                                    version.status?.toLowerCase().includes('act')
                                                        ? 'activo'
                                                        : version.status?.toLowerCase().includes('rech')
                                                        ? 'rechazado'
                                                        : 'revision'
                                                }`}
                                                value={version.status}
                                                onChange={(e) => handleTrackFieldChange('produccion', index, 'status', e.target.value)}
                                            >
                                                <option value="Activo">Activo</option>
                                                <option value="Rechazado">Rechazado</option>
                                                <option value="En revisión">En revisión</option>
                                            </select>
                                            <input
                                                type="text"
                                                className="track-input-date"
                                                value={version.date}
                                                onChange={(e) => handleTrackFieldChange('produccion', index, 'date', e.target.value)}
                                                placeholder="Fecha de lanzamiento"
                                            />
                                            <button
                                                type="button"
                                                className="btn-remove-track-version"
                                                onClick={() => handleRemoveTrackVersion('produccion', index)}
                                                title="Eliminar registro"
                                            >
                                                <FaTrashAlt />
                                            </button>
                                        </div>
                                    ))
                                )}
                            </div>
                        </div>

                        {/* PISTA DE PRUEBA INTERNA */}
                        <div className="track-container" style={{ marginTop: '2rem' }}>
                            <div className="track-header">
                                <span className="track-title">Prueba Interna</span>
                                <button
                                    type="button"
                                    className="btn-add-track-version"
                                    onClick={() => handleAddTrackVersion('prueba_interna')}
                                >
                                    + Agregar Compilación
                                </button>
                            </div>

                            <div className="track-versions-list">
                                {appConfig.google_play_tracks.prueba_interna?.length === 0 ? (
                                    <p style={{ fontStyle: 'italic', fontSize: '0.85rem', color: '#94a3b8', margin: '0 0 1rem 0' }}>
                                        No hay compilaciones registradas en Prueba Interna.
                                    </p>
                                ) : (
                                    appConfig.google_play_tracks.prueba_interna?.map((version, index) => (
                                        <div className="track-version-card-edit" key={`internal-${index}`}>
                                            <input
                                                type="text"
                                                className="track-input-short"
                                                value={version.version_code}
                                                onChange={(e) => handleTrackFieldChange('prueba_interna', index, 'version_code', e.target.value)}
                                                placeholder="Code"
                                                title="Código de compilación (versionCode)"
                                            />
                                            <input
                                                type="text"
                                                className="track-input-med"
                                                value={version.version_name}
                                                onChange={(e) => handleTrackFieldChange('prueba_interna', index, 'version_name', e.target.value)}
                                                placeholder="Name"
                                                title="Versión comercial (versionName)"
                                            />
                                            <select
                                                className={`track-select-status ${
                                                    version.status?.toLowerCase().includes('act')
                                                        ? 'activo'
                                                        : version.status?.toLowerCase().includes('rech')
                                                        ? 'rechazado'
                                                        : 'revision'
                                                }`}
                                                value={version.status}
                                                onChange={(e) => handleTrackFieldChange('prueba_interna', index, 'status', e.target.value)}
                                            >
                                                <option value="Activo">Activo</option>
                                                <option value="Rechazado">Rechazado</option>
                                                <option value="En revisión">En revisión</option>
                                            </select>
                                            <input
                                                type="text"
                                                className="track-input-date"
                                                value={version.date}
                                                onChange={(e) => handleTrackFieldChange('prueba_interna', index, 'date', e.target.value)}
                                                placeholder="Fecha de lanzamiento"
                                            />
                                            <button
                                                type="button"
                                                className="btn-remove-track-version"
                                                onClick={() => handleRemoveTrackVersion('prueba_interna', index)}
                                                title="Eliminar registro"
                                            >
                                                <FaTrashAlt />
                                            </button>
                                        </div>
                                    ))
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* CONTENIDO PESTAÑA 4: ANALÍTICA Y REPORTES EJECUTIVOS */}
            {activeMainTab === 'analitica_ejecutiva' && (
                <ReporteEjecutivoAnaliticaPanel />
            )}

            {editingUser && (
                <div className="edit-modal-backdrop" role="dialog" aria-modal="true">
                    <div className="edit-modal">
                        <header className="edit-modal-head">
                            <h3>Editar usuario</h3>
                            <button type="button" className="close-btn" onClick={closeEditModal}>×</button>
                        </header>
                        <div className="edit-modal-body">
                            <div className="tab-bar">
                                <button type="button" className={`tab-btn ${activeTab === 'perfil' ? 'active' : ''}`} onClick={() => setActiveTab('perfil')}>Perfil</button>
                                <button type="button" className={`tab-btn ${activeTab === 'campos' ? 'active' : ''}`} onClick={() => setActiveTab('campos')}>Campos personalizados</button>
                            </div>

                            {activeTab === 'perfil' && (
                                <div className="form-grid form-grid-2col">
                                    <label className="form-label">
                                        Nombre
                                        <input type="text" name="nombre" value={editForm.nombre} onChange={handleEditChange} placeholder="Ej: Juan Pérez" />
                                    </label>
                                    <label className="form-label">
                                        Teléfono
                                        <input type="tel" name="telefono" value={editForm.telefono} onChange={handleEditChange} placeholder="Ej: +52 1234567890" />
                                    </label>

                                    <label className="form-label full-width">
                                        Correo
                                        <input type="email" name="correo" value={editForm.correo} onChange={handleEditChange} placeholder="usuario@ejemplo.com" />
                                    </label>

                                    <label className="form-label">
                                        Usuario
                                        <input type="text" name="usuario" value={editForm.usuario} onChange={handleEditChange} placeholder="nombre de usuario" />
                                    </label>

                                    <label className="form-label">
                                        Rol
                                        <select name="rol" value={editForm.rol} onChange={handleEditChange} className="form-select">
                                            <option value="">Seleccionar rol...</option>
                                            <option value="platformadmin">Platform Admin (Gerente de plataforma)</option>
                                            <option value="superadmin">Super Admin (Administrador de fraccionamiento)</option>
                                            <option value="user">User (Residente)</option>
                                            <option value="caseta">Caseta (Guardia de seguridad)</option>
                                        </select>
                                    </label>

                                    <label className="form-label">
                                        Fecha autenticación
                                        <input type="text" value={formatDateTime(resolveAuthDate(editingUser))} disabled className="form-input-disabled" />
                                    </label>

                                    <label className="form-label full-width">
                                        Nueva Contraseña (Asignar directamente)
                                        <div className="password-wrapper">
                                            <input
                                                type={showPassword ? "text" : "password"}
                                                name="nuevaContrasena"
                                                value={editForm.nuevaContrasena}
                                                onChange={handleEditChange}
                                                placeholder="Dejar vacío para no cambiar"
                                            />
                                            <button
                                                type="button"
                                                className="toggle-password"
                                                onClick={() => setShowPassword((prev) => !prev)}
                                                aria-label={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                                            >
                                                {showPassword ? <FiEyeOff /> : <FiEye />}
                                            </button>
                                        </div>
                                        <span className="form-helper-text">Ingresa una contraseña para asignarla directamente. Se asignará sin enviar enlace de reestablecimiento.</span>
                                        {passwordError && <span className="form-error-text">{passwordError}</span>}
                                    </label>

                                    <label className="form-label full-width">
                                        Confirmar contraseña
                                        <div className="password-wrapper">
                                            <input
                                                type={showConfirmPassword ? "text" : "password"}
                                                name="confirmContrasena"
                                                value={editForm.confirmContrasena}
                                                onChange={handleEditChange}
                                                placeholder="Vuelve a escribir la contraseña"
                                            />
                                            <button
                                                type="button"
                                                className="toggle-password"
                                                onClick={() => setShowConfirmPassword((prev) => !prev)}
                                                aria-label={showConfirmPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                                            >
                                                {showConfirmPassword ? <FiEyeOff /> : <FiEye />}
                                            </button>
                                        </div>
                                    </label>
                                </div>
                            )}

                            {activeTab === 'campos' && (
                                <div className="custom-fields-container">
                                    <div className="cf-header-row">
                                        <div>Nombre del Campo</div>
                                        <div>Valor / Información</div>
                                        <div />
                                    </div>
                                    <div className="cf-body">
                                        {customFields.length === 0 ? (
                                            <div className="cf-empty-state">
                                                <div className="cf-empty-state-icon"><FaUsers style={{ opacity: 0.2 }} /></div>
                                                <p>No hay campos personalizados adicionales para este usuario.</p>
                                                <p style={{ fontSize: '0.8rem', marginTop: '4px' }}>Haz clic en el botón inferior para agregar uno.</p>
                                            </div>
                                        ) : (
                                            customFields.map((item, idx) => (
                                                <div className="cf-item-row" key={`cf_${idx}`}>
                                                    <input
                                                        className="cf-input-styled"
                                                        type="text"
                                                        value={item.name}
                                                        placeholder="Ej: Código Postal"
                                                        onChange={(e) => {
                                                            const v = e.target.value;
                                                            setCustomFields((prev) => prev.map((r, i) => i === idx ? { ...r, name: v } : r));
                                                        }}
                                                    />
                                                    <input
                                                        className="cf-input-styled"
                                                        type="text"
                                                        value={item.value}
                                                        placeholder="Valor o información"
                                                        onChange={(e) => {
                                                            const v = e.target.value;
                                                            setCustomFields((prev) => prev.map((r, i) => i === idx ? { ...r, value: v } : r));
                                                        }}
                                                    />
                                                    <button
                                                        type="button"
                                                        className="cf-delete-btn"
                                                        onClick={() => setCustomFields((prev) => prev.filter((_, i) => i !== idx))}
                                                        title="Eliminar campo"
                                                    >
                                                        <FaTimes />
                                                    </button>
                                                </div>
                                            ))
                                        )}
                                    </div>
                                    <div className="cf-add-footer">
                                        <button
                                            type="button"
                                            className="cf-add-pill-btn"
                                            onClick={() => setCustomFields([...customFields, { name: '', value: '' }])}
                                        >
                                            <FaPlus /> Agregar nuevo campo personalizado
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                        <footer className="edit-modal-footer">
                            <button
                                type="button"
                                className={`modal-action-btn ${editingUser.suspendido ? 'unlock' : 'lock'}`}
                                title={editingUser.suspendido ? 'Habilitar usuario' : 'Suspender usuario'}
                                onClick={() => handleToggleSuspend(editingUser)}
                            >
                                {editingUser.suspendido ? <FiUnlock /> : <FiLock />}
                                <span>{editingUser.suspendido ? 'Habilitar' : 'Suspender'}</span>
                            </button>
                            <div className="footer-right">
                                <button type="button" className="ghost-btn" onClick={closeEditModal} disabled={saving}>Cancelar</button>
                                <button type="button" className="primary-btn" onClick={handleSave} disabled={saving}>
                                    {saving ? 'Guardando…' : 'Guardar cambios'}
                                </button>
                            </div>
                        </footer>
                    </div>
                </div>
            )}

            {showPlatformAdminConfirm && (
                <div className="edit-modal-backdrop" role="dialog" aria-modal="true">
                    <div className="edit-modal confirm-modal" style={{ maxWidth: '480px' }}>
                        <header className="edit-modal-head">
                            <h3>⚠️ Confirmación - Rol de Platform Admin</h3>
                            <button type="button" className="close-btn" onClick={handleCancelPlatformAdmin}>×</button>
                        </header>
                        <div className="edit-modal-body" style={{ padding: '2rem' }}>
                            <p style={{ margin: '0 0 1rem 0', fontSize: '1rem', color: '#374151' }}>
                                Estás asignando el rol <strong>Platform Admin</strong> a <strong>{editingUser?.nombre || editingUser?.correo}</strong>.
                            </p>
                            <p style={{ margin: '0 0 1rem 0', fontSize: '1rem', color: '#374151' }}>
                                Este es el rol de <strong>Gerente de Plataforma</strong> con máximos permisos. ¿Estás seguro?
                            </p>
                            <div style={{ backgroundColor: '#FEF3C7', border: '1px solid #FCD34D', borderRadius: '8px', padding: '1rem', marginBottom: '1.5rem' }}>
                                <p style={{ margin: '0', fontSize: '0.9rem', color: '#92400E' }}>
                                    El usuario podrá gestionar todos los fraccionamientos y usuarios de la plataforma.
                                </p>
                            </div>
                        </div>
                        <footer className="edit-modal-footer">
                            <button type="button" className="ghost-btn" onClick={handleCancelPlatformAdmin}>Cancelar</button>
                            <button type="button" className="primary-btn" onClick={handleConfirmPlatformAdmin} style={{ backgroundColor: '#d97706' }}>
                                Confirmar - Asignar Platform Admin
                            </button>
                        </footer>
                    </div>
                </div>
            )}

            {/* Modal de confirmación estilizado */}
            <ConfirmModal
                isOpen={confirmModal.isOpen}
                title={confirmModal.title}
                message={confirmModal.message}
                type={confirmModal.type}
                confirmText={confirmModal.confirmText}
                showCancel={confirmModal.showCancel}
                onConfirm={confirmModal.onConfirm}
                onCancel={() => setConfirmModal({ ...confirmModal, isOpen: false })}
            />
        </div>
    );
};

export default ControlSesiones;
